import React, { memo, useEffect, useMemo } from "react";
import shallow from "zustand/shallow";
import CustomModal from "../CustomModal/CustomModal";
import DefaultIcon from "../../../assets/icons/default_icon.jpg";
import { useStoreRoom, useStoreSystem, TireRackProductI } from "../../../store";
import Stack from "@mui/material/Stack";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import styles from "./TireRacksModal.module.scss";

import { Button, CircularProgress } from "@mui/material";
import { ToggleButton } from "../../shared/ui/ToggleButton";

import { ConstructionElement } from "../../../controllers/types";
import { isNewSite } from "../../../utils";

const isNew = isNewSite();

export const TireRacksModal = memo(() => {
    const { addTireRackAction } = useStoreRoom(
        (state) => ({
            addTireRackAction: state.addTireRack,
        }),
        shallow
    );

    const {
        tireRacksModal,
        setTireRacksModal,
        currentConstructions,
        currentConstructionCategoryName,
        parts,
        tireRacksFromProducts,
        loadingTireRacksFromProducts,
        loadTireRacksFromProducts,
    } = useStoreSystem(
        (state) => ({
            tireRacksModal: state.tireRacksModal,
            setTireRacksModal: state.setTireRacksModal,
            currentConstructions: state.currentConstructions,
            currentConstructionCategoryName: state.currentConstructionCategoryName,
            parts: state.parts,
            tireRacksFromProducts: state.tireRacksFromProducts,
            loadingTireRacksFromProducts: state.loadingTireRacksFromProducts,
            loadTireRacksFromProducts: state.loadTireRacksFromProducts,
        }),
        shallow
    );

    // Загружаем данные из новой функции при открытии модала
    // useEffect(() => {
    //     if (tireRacksModal.isOpen) {
    //         loadTireRacksFromProducts();
    //     }
    // }, [tireRacksModal.isOpen, loadTireRacksFromProducts]);

    // Переключатель между старыми конструкциями и новыми продуктами
    const [useNewProducts, setUseNewProducts] = React.useState(false);

    // Фильтрация по конфигурациям (1x4, 2x2, 2x4, 1x8) для старых конструкций
    const configurations = Array.from(new Set(currentConstructions.map((item) => item.PROPERTIES.CONFIGURATION)));
    const [selectedConfigs, setSelectedConfigs] = React.useState<string[]>(configurations);

    const handleChangeConfigs = (event: React.MouseEvent<HTMLElement>, newConfigs: string[]) => {
        if (newConfigs.length > 0) {
            setSelectedConfigs(newConfigs);
        }
    };

    const filtered = currentConstructions.filter((construction) =>
        selectedConfigs.includes(construction.PROPERTIES.CONFIGURATION)
    );

    // Группируем элементы по категориям и берем первый элемент из каждой категории
    const categoryItems = useMemo(() => {
        const categoryMap = new Map<string, ConstructionElement>();

        filtered.forEach((element) => {
            const category = element.PROPERTIES.CATEGORY;
            if (category && !categoryMap.has(category)) {
                categoryMap.set(category, element);
            }
        });

        return Array.from(categoryMap.values());
    }, [filtered]);

    // Определяем что показывать - категории или все элементы
    const itemsToShow = categoryItems.length > 0 ? categoryItems : filtered;

    const getTotalPrice = (construction: ConstructionElement) => {
        let totalPrice = construction.PRICE;

        if (construction.PARTS) {
            construction.PARTS.forEach((part) => {
                totalPrice += part.price ?? 0;
            });
        }

        if (isNew) {
            return Math.min(...construction?.newNomenkl?.map?.((n) => (n?.priceNomenkl || 0) as number)) || totalPrice;
        } else {
            return totalPrice;
        }
    };

    const getProductPrice = (product: TireRackProductI) => {
        return product.PRICE?.VALUE || 0;
    };

    const handleAddTireRack = (construction: ConstructionElement) => () => {
        addTireRackAction(construction);
        setTireRacksModal(false, undefined);
    };

    const handleAddProductTireRack = (product: TireRackProductI) => () => {
        // Преобразуем продукт в формат ConstructionElement для совместимости
        // @ts-ignore
        const constructionElement: ConstructionElement = {
            ID: product.ID,
            NAME: product.NAME,
            CODE: product.CODE,
            PRICE: product.PRICE?.VALUE || 0,
            OLD_PRICE: product.PRICE?.VALUE || 0,
            PICTURE: product.PREVIEW_PICTURE || product.DETAIL_PICTURE || "",
            BITRIX_OFFER_ID: parseInt(product.ID),
            PROPERTIES: {
                FILE_3D: "",
                CONFIGURATION: product.PROPERTIES.CONFIGURATION?.VALUE || "1x1",
                HEIGHT: product.PROPERTIES.HEIGHT?.VALUE || "0",
                WIDTH: product.PROPERTIES.WIDTH?.VALUE || "0",
                DEPTH: product.PROPERTIES.DEPTH?.VALUE || "0",
            },
            PARTS: [],
        };
        addTireRackAction(constructionElement);
        setTireRacksModal(false, undefined);
    };

    return (
        <CustomModal
            isOpenModal={tireRacksModal.isOpen}
            onCloseModalCallback={() => {
                setTireRacksModal(false, undefined);
            }}
        >
            <div className={styles.root}>
                <Stack direction="row" spacing={4} alignItems="center">
                    <span>Стойки-стеллажи для колёс и шин</span>
                </Stack>

                {loadingTireRacksFromProducts && useNewProducts ? (
                    <div className={styles.emptyGrid}>
                        <CircularProgress />
                        <div>Загрузка товаров...</div>
                    </div>
                ) : (
                    <div
                        className={
                            (useNewProducts ? tireRacksFromProducts.length : itemsToShow.length)
                                ? styles.grid
                                : styles.emptyGrid
                        }
                    >
                        {useNewProducts
                            ? // Отображение новых продуктов из каталога
                              tireRacksFromProducts.map((product) => (
                                  <div key={product.ID} className={styles.item}>
                                      <img
                                          className={styles.itemImg}
                                          src={product.PREVIEW_PICTURE || product.DETAIL_PICTURE || DefaultIcon}
                                          alt={product.NAME}
                                          width={100}
                                      />
                                      <div className={styles.itemName}>{product.NAME}</div>
                                      <div className={styles.itemPrice}>{getProductPrice(product)} руб</div>
                                      {product.QUANTITY !== undefined && (
                                          <div className={styles.itemQuantity}>Остаток: {product.QUANTITY}</div>
                                      )}
                                      <Button
                                          className={styles.itemBtn}
                                          variant="contained"
                                          onClick={handleAddProductTireRack(product)}
                                          disabled={product.AVAILABLE === "N"}
                                      >
                                          Добавить
                                      </Button>
                                  </div>
                              ))
                            : // Отображение конструкций (группированных по категориям)
                              itemsToShow.map((construction) => (
                                  <div key={construction.ID} className={styles.item}>
                                      <img
                                          className={styles.itemImg}
                                          src={construction.PICTURE || DefaultIcon}
                                          alt={construction.NAME}
                                          width={100}
                                      />
                                      <div className={styles.itemName}>
                                          {construction.PROPERTIES.CATEGORY || construction.NAME}
                                      </div>
                                      <div className={styles.itemPrice}>от {getTotalPrice(construction)} руб</div>
                                      <Button
                                          className={styles.itemBtn}
                                          variant="contained"
                                          onClick={handleAddTireRack(construction)}
                                      >
                                          Добавить
                                      </Button>
                                  </div>
                              ))}
                        {((useNewProducts && tireRacksFromProducts.length === 0) ||
                            (!useNewProducts && itemsToShow.length === 0)) &&
                            !loadingTireRacksFromProducts && (
                                <div className={styles.notFoundElements}>Элементов не найдено</div>
                            )}
                    </div>
                )}
            </div>
        </CustomModal>
    );
});
